import { FunctionComponent, useMemo } from "react";

import { isGUID } from "@bps/common-utils";
import {
  Dialog,
  FieldSpy,
  OptionsSelectField,
  SubmissionForm,
  TextInputField
} from "@bps/fluent-ui";
import { Validator } from "@bps/validator";
import {
  useAddOrUpdateComponent,
  useComponentsDefs
} from "@libs/api/gateways/plt/plt-gateway.hooks";
import { ValidationMessages } from "@libs/validation/validation-messages.enum";

import {
  CreateComponentDialogFormValues,
  CreateComponentDialogProps
} from "./CreateComponentDialog.types";

const createCompValidator = new Validator<CreateComponentDialogFormValues>(
  validator => ({
    code: validator.string().required(),
    scopeId: [
      validator.string().required(),
      validator
        .custom()
        .func<"scopeId">(value =>
          isGUID(value ?? "") ? value : ValidationMessages.guid
        )
    ]
  })
);

export const CreateComponentDialog: FunctionComponent<CreateComponentDialogProps> = ({
  tenantId,
  onDismiss
}) => {
  const { data = [], isLoading, error } = useComponentsDefs();
  const { mutateAsync } = useAddOrUpdateComponent(
    "A new component has been added!"
  );

  const options = useMemo(
    () => data.map(d => ({ key: d.code, text: d.text })),
    [data]
  );

  const isTenantScope = (code: string) => {
    const component = data.find(x => x.code == code);
    return component?.scope === "Tenant";
  };

  return (
    <Dialog
      hidden={false}
      onDismiss={onDismiss}
      dialogContentProps={{ title: "Create component", showCloseButton: true }}
    >
      <SubmissionForm<CreateComponentDialogFormValues>
        initialValues={{}}
        onSubmit={async values => {
          await mutateAsync({
            tenantId,
            code: values.code!,
            scopeId: values.scopeId
          });
          onDismiss();
        }}
        cancelButtonProps={{ onClick: onDismiss }}
        validate={createCompValidator.validate}
      >
        {(_, { setValue }) => (
          <>
            <OptionsSelectField
              name="code"
              loading={isLoading}
              errorMessage={error?.message}
              options={options}
              required
              label="Component code"
            />

            <FieldSpy
              name="code"
              onChange={code => {
                if (isTenantScope(code)) {
                  setValue && setValue("scopeId", tenantId);
                }
              }}
            />
            <FieldSpy name="code">
              {code => {
                return (
                  <TextInputField
                    name="scopeId"
                    label="Scope ID"
                    required
                    readOnly={isTenantScope(code)}
                  />
                );
              }}
            </FieldSpy>
          </>
        )}
      </SubmissionForm>
    </Dialog>
  );
};
